import React, { useEffect, useMemo, useState, useCallback } from "react";
import { PageHeader } from "./Pengumuman";
import { PiggyBank, ArrowUpRight, ArrowDownRight, Plus, Edit3, Trash2, Search, FileDown, FileSpreadsheet, FileText, Settings2, FileType, Upload } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { kasAPI, kasChartAPI } from "../lib/api";
import { useApp } from "../context/AppContext";
import { getWIBParts } from "../components/WIBClock";
import { exportToExcel, exportToPDF, exportToWord, importFromExcel } from "../lib/exportUtils";

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);
const BULAN_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const KATEGORI_OPTIONS = ["Iuran K3", "Sumbangan 17an", "Gaji Satpam", "Kasbon", "Operasional", "Perbaikan", "HUT RI", "Konsumsi", "Utilitas", "Retribusi", "Admin Bank", "Sosial", "Transfer Awal", "Catatan", "Lainnya"];

const kategoriColor = (k) => {
  const map = {
    "Iuran K3": "bg-emerald-100 text-emerald-800",
    "Sumbangan 17an": "bg-red-100 text-red-800",
    "Gaji Satpam": "bg-blue-100 text-blue-800",
    "Kasbon": "bg-orange-100 text-orange-800",
    "Operasional": "bg-slate-100 text-slate-700",
    "Perbaikan": "bg-cyan-100 text-cyan-800",
    "HUT RI": "bg-red-100 text-red-800",
    "Konsumsi": "bg-yellow-100 text-yellow-800",
    "Utilitas": "bg-purple-100 text-purple-800",
    "Retribusi": "bg-pink-100 text-pink-800",
    "Admin Bank": "bg-gray-100 text-gray-600",
    "Sosial": "bg-teal-100 text-teal-800",
    "Transfer Awal": "bg-emerald-100 text-emerald-800",
    "Catatan": "bg-amber-100 text-amber-800",
  };
  return map[k] || "bg-slate-100 text-slate-700";
};

const currentMonthValue = () => {
  const p = getWIBParts();
  const mIdx = BULAN_ID.indexOf(p.bulan) + 1;
  return `${p.tahun}-${String(mIdx).padStart(2, "0")}`;
};

const MONTH_OPTIONS = (() => {
  const arr = [{ value: "all", label: "Semua Bulan" }];
  for (const y of [2025, 2026]) for (let m = 1; m <= 12; m++) arr.push({ value: `${y}-${String(m).padStart(2, "0")}`, label: `${BULAN_ID[m - 1]} ${y}` });
  return arr;
})();

const emptyForm = { tanggal: "", keterangan: "", jenis: "masuk", jumlah: "", kategori: "Iuran K3" };

const KasRT = () => {
  const { user, theme, updateTheme } = useApp();
  const canEdit = user.role === "admin" || user.role === "super_admin";
  const isSuper = user.role === "super_admin";
  const [kas, setKas] = useState({ saldoAwal: 0, pemasukan: 0, pengeluaran: 0, saldoAkhir: 0, transaksi: [] });
  const [chartData, setChartData] = useState([]);
  const [bulan, setBulan] = useState(currentMonthValue());
  const [q, setQ] = useState("");
  const [kategoriFilter, setKategoriFilter] = useState("all");
  const [jenisFilter, setJenisFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(() => {
    const b = bulan === "all" ? undefined : bulan;
    kasAPI.get(b).then(setKas).catch(() => {});
    kasChartAPI.get(b).then(setChartData).catch(() => {});
  }, [bulan]);
  useEffect(() => {
    load();
    // Realtime auto-refresh every 25 seconds and on window focus
    const iv = setInterval(load, 8000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const filtered = useMemo(() => {
    return (kas.transaksi || []).filter((t) => {
      const okQ = !q || t.keterangan.toLowerCase().includes(q.toLowerCase());
      const okK = kategoriFilter === "all" || t.kategori === kategoriFilter;
      const okJ = jenisFilter === "all" || t.jenis === jenisFilter;
      return okQ && okK && okJ;
    });
  }, [kas, q, kategoriFilter, jenisFilter]);

  const openNew = () => {
    setEditing(null);
    const p = getWIBParts();
    const mIdx = BULAN_ID.indexOf(p.bulan) + 1;
    const today = `${p.tahun}-${String(mIdx).padStart(2, "0")}-${String(p.tanggal).padStart(2, "0")}`;
    setForm({ ...emptyForm, tanggal: today });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ tanggal: t.tanggal, keterangan: t.keterangan, jenis: t.jenis, jumlah: String(t.jumlah), kategori: t.kategori || "Lainnya" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.tanggal || !form.keterangan || !form.jumlah) { toast.error("Tanggal, keterangan, jumlah wajib"); return; }
    try {
      const payload = { ...form, jumlah: parseInt(form.jumlah, 10) };
      if (editing) { await kasAPI.updateTx(editing.id, payload); toast.success("Transaksi diperbarui"); }
      else { await kasAPI.addTx(payload); toast.success("Transaksi ditambahkan"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus transaksi ini?")) return;
    try { await kasAPI.deleteTx(id); toast.success("Transaksi dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const bulanLabel = () => (bulan === "all" ? "Semua Bulan" : (MONTH_OPTIONS.find((m) => m.value === bulan)?.label || bulan));

  const buildExportRows = () => withRunning.map((t, i) => [
    i + 1,
    t.tanggal,
    t.kategori || "Lainnya",
    t.keterangan,
    t.jenis === "keluar" ? t.jumlah : 0,
    t.jenis === "masuk" ? t.jumlah : 0,
    t.saldoRunning,
  ]);

  const exportCSV = () => {
    const header = ["No", "Tanggal", "Kategori", "Uraian", "Debet (Keluar)", "Kredit (Masuk)"];
    const rows = filtered.map((t, i) => [i + 1, t.tanggal, t.kategori, `"${t.keterangan.replace(/"/g, '""')}"`, t.jenis === "keluar" ? t.jumlah : "", t.jenis === "masuk" ? t.jumlah : ""]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `kas-rt-${bulan}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV diunduh");
  };

  const exportExcel = () => {
    const rows = buildExportRows();
    const totalMasuk = withRunning.filter((t) => t.jenis === "masuk").reduce((s, t) => s + t.jumlah, 0);
    const totalKeluar = withRunning.filter((t) => t.jenis === "keluar").reduce((s, t) => s + t.jumlah, 0);
    const saldoAkhir = withRunning.length ? withRunning[withRunning.length - 1].saldoRunning : kas.saldoAwal;
    exportToExcel({
      filename: `kas-rt-${bulan}.xlsx`,
      sheetName: `Kas RT ${bulanLabel()}`.slice(0, 31),
      headers: ["No", "Tanggal", "Kategori", "Uraian", "Debet (Rp)", "Kredit (Rp)", "Saldo (Rp)"],
      rows,
      summaryRows: [
        ["", "", "", "SALDO AWAL", "", "", kas.saldoAwal],
        ["", "", "", "TOTAL DEBET (KELUAR)", totalKeluar, "", ""],
        ["", "", "", "TOTAL KREDIT (MASUK)", "", totalMasuk, ""],
        ["", "", "", "SALDO AKHIR", "", "", saldoAkhir],
      ],
    });
    toast.success("Excel diunduh");
  };

  const exportPDF = () => {
    const rows = buildExportRows();
    const totalMasuk = withRunning.filter((t) => t.jenis === "masuk").reduce((s, t) => s + t.jumlah, 0);
    const totalKeluar = withRunning.filter((t) => t.jenis === "keluar").reduce((s, t) => s + t.jumlah, 0);
    const saldoAkhir = withRunning.length ? withRunning[withRunning.length - 1].saldoRunning : kas.saldoAwal;
    exportToPDF({
      filename: `kas-rt-${bulan}.pdf`,
      title: `LAPORAN KAS RT — ${bulanLabel()}`,
      subtitle: theme?.appTagline || "Rincian Pemasukan & Pengeluaran per Transaksi",
      orgName: (theme?.appName || "WARGA RT DM RESIDENCE").toUpperCase(),
      headers: ["No", "Tanggal", "Kategori", "Uraian", "Debet (Rp)", "Kredit (Rp)", "Saldo (Rp)"],
      rows,
      summary: [
        ["Saldo Awal", formatIDR(kas.saldoAwal)],
        ["Total Debet", formatIDR(totalKeluar)],
        ["Total Kredit", formatIDR(totalMasuk)],
        ["Saldo Akhir", formatIDR(saldoAkhir)],
        ["Jumlah Transaksi", `${rows.length}`],
        ["Periode", bulanLabel()],
      ],
      orientation: "landscape",
      footer: theme?.footerText || "",
      numericCols: [4, 5, 6],
    });
    toast.success("PDF diunduh");
  };

  // Running balance calculation for the filtered list
  const withRunning = useMemo(() => {
    let bal = kas.saldoAwal || 0;
    return filtered.map((t) => {
      bal += t.jenis === "masuk" ? t.jumlah : -t.jumlah;
      return { ...t, saldoRunning: bal };
    });
  }, [filtered, kas.saldoAwal]);

  return (
    <div>
      <PageHeader title="Kas RT" subtitle="Laporan keuangan warga RT DM Residence (rinci per transaksi)" icon={PiggyBank}
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportPDF} data-testid="kas-export-pdf-btn"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" onClick={exportExcel} data-testid="kas-export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" onClick={() => {
            const rows = buildExportRows();
            exportToWord({ filename: `kas-rt-${bulan}.doc`, title: `LAPORAN KAS RT — ${bulanLabel()}`,
              headers: ["No", "Tanggal", "Kategori", "Uraian", "Debet", "Kredit", "Saldo"], rows });
            toast.success("Word diunduh");
          }} data-testid="kas-export-word-btn"><FileType className="h-4 w-4 mr-1" />Word</Button>
          <Button variant="outline" onClick={exportCSV} data-testid="kas-export-csv-btn"><FileDown className="h-4 w-4 mr-1" />CSV</Button>
          {canEdit && <>
            <input type="file" accept=".xlsx,.xls,.csv" id="kas-import" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              try {
                const rows = await importFromExcel(f);
                if (!rows.length) { toast.error("File kosong"); return; }
                if (!window.confirm(`Import ${rows.length} transaksi Kas?`)) return;
                let ok = 0, fail = 0;
                for (const r of rows) {
                  const tanggal = String(r.tanggal || r.Tanggal || "").trim();
                  const keterangan = String(r.keterangan || r.uraian || r.Keterangan || "").trim();
                  const jumlah = parseInt(String(r.jumlah || r.Jumlah || r.nominal || 0).replace(/[^0-9]/g, ""), 10);
                  const jenis = String(r.jenis || r.Jenis || "masuk").toLowerCase().trim();
                  const kategori = String(r.kategori || r.Kategori || "Lainnya").trim();
                  if (!tanggal || !keterangan || !jumlah) { fail++; continue; }
                  try { await kasAPI.createTx({ tanggal, keterangan, jumlah, jenis: jenis === "keluar" ? "keluar" : "masuk", kategori }); ok++; }
                  catch { fail++; }
                }
                toast.success(`Import selesai: ${ok} sukses, ${fail} gagal`);
                load();
              } catch (err) { toast.error(err.message || "File tidak valid"); }
              e.target.value = "";
            }} />
            <Label htmlFor="kas-import" className="cursor-pointer" data-testid="kas-import-label">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold h-9"><Upload className="h-3.5 w-3.5" />Import</span>
            </Label>
          </>}
          {canEdit && <Button className="brand-btn" onClick={openNew} data-testid="kas-add-tx-btn"><Plus className="h-4 w-4 mr-1" />Tambah Transaksi</Button>}
        </div>}
      />

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-slate-500 font-semibold">SALDO AWAL BULAN</div>
              <div className="text-xl font-extrabold text-slate-800 mt-1" data-testid="kas-saldo-awal">{formatIDR(kas.saldoAwal)}</div>
              <div className="text-[10px] text-slate-400 mt-1">Opening Ledger: {formatIDR(kas.ledgerOpening || 0)}</div>
            </div>
            {isSuper && <button
              className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-emerald-700"
              title="Edit Saldo Awal Ledger"
              data-testid="kas-edit-saldo-awal"
              onClick={async () => {
                const v = window.prompt("Saldo Awal Ledger (opening balance sebelum transaksi apapun). Masukkan angka rupiah tanpa titik/koma:", String(kas.ledgerOpening || 0));
                if (v === null) return;
                const n = parseInt(v.replace(/[^0-9-]/g, ""), 10);
                if (isNaN(n)) { toast.error("Input harus angka"); return; }
                try {
                  await updateTheme({ saldoAwalKas: n });
                  toast.success("Saldo Awal Ledger disimpan");
                  load();
                } catch { toast.error("Gagal simpan"); }
              }}
            ><Settings2 className="h-4 w-4" /></button>}
          </div>
        </CardContent></Card>
        <Card className="border-emerald-100"><CardContent className="p-5"><div className="text-xs text-emerald-700 font-semibold">PEMASUKAN (KREDIT)</div><div className="text-xl font-extrabold text-emerald-700 mt-1">{formatIDR(kas.pemasukan)}</div></CardContent></Card>
        <Card className="border-red-100"><CardContent className="p-5"><div className="text-xs text-red-700 font-semibold">PENGELUARAN (DEBET)</div><div className="text-xl font-extrabold text-red-700 mt-1">{formatIDR(kas.pengeluaran)}</div></CardContent></Card>
        <Card className="border-amber-100"><CardContent className="p-5"><div className="text-xs text-amber-700 font-semibold">SALDO AKHIR (TOTAL)</div><div className="text-xl font-extrabold text-amber-700 mt-1">{formatIDR(kas.saldoAkhir)}</div><div className="text-[10px] text-slate-500 mt-1">Kumulatif semua bulan</div></CardContent></Card>
      </div>

      {/* CHART PER KATEGORI */}
      {chartData.length > 0 && (
        <Card className="mb-6"><CardContent className="p-6">
          <h3 className="font-bold text-slate-800 mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>Grafik Pemasukan vs Pengeluaran per Kategori</h3>
          {(() => {
            const maxVal = Math.max(1, ...chartData.map((c) => Math.max(c.masuk, c.keluar)));
            return (
              <div className="space-y-3">
                {chartData.map((c) => (
                  <div key={c.kategori} className="grid grid-cols-[140px_1fr_1fr] gap-3 items-center">
                    <div className="text-xs font-semibold text-slate-700 truncate" title={c.kategori}>{c.kategori}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-6 bg-emerald-50 rounded-md overflow-hidden">
                        <div className="h-full bg-emerald-500 flex items-center justify-end pr-2 text-[10px] text-white font-bold transition-all" style={{ width: `${(c.masuk / maxVal) * 100}%` }}>{c.masuk > 0 && formatIDR(c.masuk)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-6 bg-red-50 rounded-md overflow-hidden">
                        <div className="h-full bg-red-500 flex items-center justify-end pr-2 text-[10px] text-white font-bold transition-all" style={{ width: `${(c.keluar / maxVal) * 100}%` }}>{c.keluar > 0 && formatIDR(c.keluar)}</div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-end gap-4 pt-2 text-xs">
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-500" />Pemasukan</span>
                  <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-red-500" />Pengeluaran</span>
                </div>
              </div>
            );
          })()}
        </CardContent></Card>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari uraian..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Select value={bulan} onValueChange={setBulan}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[300px]">{MONTH_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={jenisFilter} onValueChange={setJenisFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="masuk">Pemasukan</SelectItem><SelectItem value="keluar">Pengeluaran</SelectItem></SelectContent>
        </Select>
        <Select value={kategoriFilter} onValueChange={setKategoriFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{KATEGORI_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="text-xs text-slate-500 mb-2">Menampilkan <b className="text-slate-700">{withRunning.length}</b> transaksi</div>

      <Card className="overflow-hidden"><CardContent className="p-0">
        <div className="overflow-x-auto max-h-[65vh] scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="bg-emerald-700 text-white text-xs uppercase sticky top-0 z-10">
              <tr>
                <th className="text-center p-3 w-12">No</th>
                <th className="text-left p-3 w-28">Tanggal</th>
                <th className="text-left p-3 w-32">Kategori</th>
                <th className="text-left p-3">Uraian</th>
                <th className="text-right p-3 w-32">Debet</th>
                <th className="text-right p-3 w-32">Kredit</th>
                <th className="text-right p-3 w-36">Saldo</th>
                {canEdit && <th className="text-right p-3 w-20">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {withRunning.map((t, i) => (
                <tr key={t.id} className="border-t border-emerald-50 hover:bg-emerald-50/30">
                  <td className="p-3 text-center text-slate-500">{i + 1}</td>
                  <td className="p-3 text-slate-700 whitespace-nowrap">{t.tanggal}</td>
                  <td className="p-3"><Badge variant="secondary" className={`${kategoriColor(t.kategori)} hover:${kategoriColor(t.kategori)} text-xs`}>{t.kategori || "Lainnya"}</Badge></td>
                  <td className="p-3 text-slate-800">{t.keterangan}</td>
                  <td className="p-3 text-right text-red-700 font-semibold">{t.jenis === "keluar" ? formatIDR(t.jumlah) : ""}</td>
                  <td className="p-3 text-right text-emerald-700 font-semibold">{t.jenis === "masuk" ? formatIDR(t.jumlah) : ""}</td>
                  <td className="p-3 text-right font-bold text-slate-800">{formatIDR(t.saldoRunning)}</td>
                  {canEdit && <td className="p-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Edit3 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                  </td>}
                </tr>
              ))}
              {withRunning.length === 0 && <tr><td colSpan={canEdit ? 8 : 7} className="p-8 text-center text-slate-400">Tidak ada transaksi sesuai filter</td></tr>}
            </tbody>
            <tfoot className="bg-emerald-50 font-bold sticky bottom-0">
              <tr>
                <td colSpan={4} className="p-3 text-right text-slate-700">TOTAL BULAN</td>
                <td className="p-3 text-right text-red-700">{formatIDR(withRunning.filter(t => t.jenis === "keluar").reduce((s, t) => s + t.jumlah, 0))}</td>
                <td className="p-3 text-right text-emerald-700">{formatIDR(withRunning.filter(t => t.jenis === "masuk").reduce((s, t) => s + t.jumlah, 0))}</td>
                <td className="p-3 text-right text-amber-800">{formatIDR(withRunning.length ? withRunning[withRunning.length - 1].saldoRunning : kas.saldoAwal)}</td>
                {canEdit && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Transaksi" : "Transaksi Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal</Label><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
              <div><Label>Jenis</Label>
                <Select value={form.jenis} onValueChange={(v) => setForm({ ...form, jenis: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="masuk">Pemasukan (Kredit)</SelectItem><SelectItem value="keluar">Pengeluaran (Debet)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Uraian / Keterangan</Label><Input value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} placeholder="Contoh: Titipan K3 Blok A1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jumlah (Rp)</Label><Input type="number" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} /></div>
              <div><Label>Kategori</Label>
                <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">{KATEGORI_OPTIONS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} className="brand-btn">{editing ? "Simpan" : "Tambah"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default KasRT;
